"use client";

import type { FC } from "react";
import { PhoneIcon, UserIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/cn";
import { AttributePill } from "./attribute-pill";
import { HorizontalScroll } from "./horizontal-scroll";

type PractitionerTagsProps = {
  languages?: string[];
  formats?: string[];
  size?: "s" | "m";
  hideIcons?: boolean;
  scrollable?: boolean;
  className?: string;
};

type PractitionerFormat = "in-person" | "telehealth";

const FORMAT_META: Record<PractitionerFormat, { label: string; icon: FC<{ className?: string }> }> = {
  "in-person": { label: "In-Person", icon: UserIcon },
  telehealth: { label: "Telehealth", icon: PhoneIcon },
};

export const PractitionerTags: FC<PractitionerTagsProps> = ({
  languages,
  formats,
  size = "m",
  hideIcons,
  scrollable,
  className,
}) => {
  const cleanedLanguages = (languages ?? []).filter((item) => Boolean(item?.trim()));
  const cleanedFormats = (formats ?? []).filter((format): format is PractitionerFormat => format in FORMAT_META);

  if (cleanedLanguages.length === 0 && cleanedFormats.length === 0) return null;

  const Row = scrollable ? HorizontalScroll : "div";
  const pillClassName = scrollable ? "shrink-0" : undefined;

  return (
    <Row className={cn("flex items-center gap-8", !scrollable && "flex-wrap", className)}>
      {cleanedLanguages.map((language, index) => (
        <AttributePill key={`lang-${language}-${index}`} tone="ice" size={size} className={pillClassName}>
          {language}
        </AttributePill>
      ))}
      {cleanedFormats.map((format, index) => {
        const { label, icon: Icon } = FORMAT_META[format];
        return (
          <AttributePill
            key={`format-${format}-${index}`}
            tone="rose"
            size={size}
            className={pillClassName}
            icon={hideIcons ? undefined : <Icon className="size-18" />}
          >
            {label}
          </AttributePill>
        );
      })}
    </Row>
  );
};
